import { Anchor, Text, Tooltip } from '@mantine/core';
import { IconLink } from '@tabler/icons-react';

import { isInstanceReference } from '#core/lib/cognite/types.ts';

import classes from './LinkedAssetCell.module.css';

export interface LinkedAssetCellProps {
  value: { space: string; externalId: string } | string;
  onClick?: (assetRef: { space: string; externalId: string }) => void;
}

/**
 * Renders a linked asset reference as a clickable link or plain text.
 * - Object values with space/externalId render as clickable anchors
 * - String values render as plain text
 * - Long externalIds are truncated with tooltip showing full value
 */
export function LinkedAssetCell({ value, onClick }: LinkedAssetCellProps) {
  // Handle string values - just render as text
  if (typeof value === 'string') {
    return <Text size="sm">{value}</Text>;
  }

  // Handle linked asset references
  if (isInstanceReference(value)) {
    const displayText = value.externalId;
    const tooltipContent = `${value.space}/${value.externalId}`;
    const shouldTruncate = displayText.length > 32;

    const content = (
      <Anchor
        className={classes.link}
        onClick={onClick ? () => onClick(value) : undefined}
        size="sm"
        style={{ cursor: onClick ? 'pointer' : 'default' }}
        underline="hover"
      >
        <IconLink className={classes.icon} size={14} />
        <span className={shouldTruncate ? classes.truncate : undefined}>{displayText}</span>
      </Anchor>
    );

    if (shouldTruncate) {
      return (
        <Tooltip label={tooltipContent} multiline openDelay={300} w={300} withArrow>
          {content}
        </Tooltip>
      );
    }

    return content;
  }

  // Fallback for unexpected value types
  return <Text size="sm">{String(value)}</Text>;
}
